/**
 * API origins. The same image serves every environment, so nothing is baked in at build time:
 * on `<sub>.<domain>` (`app.`, `customer.`, …) the SPA talks to `auth.<domain>` (Identity) and
 * `api.<domain>/portal` (the portal API). Bare hosts and IP addresses are development, which uses
 * localhost defaults. `VITE_AUTH_URL` / `VITE_API_URL` override either case.
 */
export interface PortalConfig {
  authUrl: string;
  apiUrl: string;
  /** The AI website builder (a separate app), where websites are designed and published. */
  builderUrl: string;
  /** Website Runtime's public API; external sites upload their files to it directly. */
  runtimeUrl: string;
  /** The public marketing site (`<domain>`): terms, privacy and other policies live there. */
  marketingUrl: string;
  /** How long before the session ends the "you're about to be signed out" warning shows (seconds). */
  sessionWarningSeconds: number;
}

/** The default warning lead time: two minutes (PHP-3322). */
export const DEFAULT_SESSION_WARNING_SECONDS = 120;

/** A positive number of seconds from an env value, else the default. Decimals are fine (`1.5`). */
function warningSeconds(raw: string | undefined): number {
  const n = Number(raw);
  return raw && Number.isFinite(n) && n > 0 ? n : DEFAULT_SESSION_WARNING_SECONDS;
}

export function deriveConfig(
  location: Pick<Location, "protocol" | "hostname" | "port">,
  env: {
    VITE_AUTH_URL?: string;
    VITE_API_URL?: string;
    VITE_BUILDER_URL?: string;
    VITE_RUNTIME_URL?: string;
    VITE_MARKETING_URL?: string;
    VITE_SESSION_WARNING_SECONDS?: string;
  } = {},
): PortalConfig {
  const trim = (u: string) => u.replace(/\/+$/, "");
  const { protocol, hostname, port } = location;
  const labels = hostname.split(".");
  const isIp = /^[\d.]+$/.test(hostname);
  if (!isIp && labels.length >= 3) {
    const base = labels.slice(1).join(".") + (port ? `:${port}` : "");
    return {
      authUrl: trim(env.VITE_AUTH_URL || `${protocol}//auth.${base}`),
      apiUrl: trim(env.VITE_API_URL || `${protocol}//api.${base}/portal`),
      builderUrl: trim(env.VITE_BUILDER_URL || `${protocol}//builder.${base}`),
      runtimeUrl: trim(env.VITE_RUNTIME_URL || `${protocol}//runtime.${base}`),
      marketingUrl: trim(env.VITE_MARKETING_URL || `${protocol}//${base}`),
      sessionWarningSeconds: warningSeconds(env.VITE_SESSION_WARNING_SECONDS),
    };
  }
  return {
    authUrl: trim(env.VITE_AUTH_URL || "http://localhost:8000"),
    apiUrl: trim(env.VITE_API_URL || "http://localhost:8001"),
    builderUrl: trim(env.VITE_BUILDER_URL || "http://localhost:3000"),
    runtimeUrl: trim(env.VITE_RUNTIME_URL || "http://localhost:8002"),
    // the published policies, which development has no copy of
    marketingUrl: trim(env.VITE_MARKETING_URL || "https://cortexgrip.com"),
    sessionWarningSeconds: warningSeconds(env.VITE_SESSION_WARNING_SECONDS),
  };
}

export const config: PortalConfig = deriveConfig(window.location, import.meta.env);
